"use client";

import { Alert, Box, Chip, Link, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";

import { formatCountdown, useCountdown } from "@/hooks/useCountdown";
import { OnboardingFormWrapper } from "../../layout";
import { FormHeader } from "../../layout/FormHeader";
import { AuthSupportFooter } from "./AuthSupportFooter";

/**
 * Mirrors TOKEN_TTL_SECONDS in sls/lambdas/requestMagicLink. The countdown is
 * an approximation: the mutation deliberately returns a bare boolean so it
 * can't be used to probe which addresses have accounts, which means the real
 * expiry never reaches the client.
 */
const LINK_TTL_SECONDS = 15 * 60;
const RESEND_COOLDOWN_SECONDS = 60;

// Onboarding verification and returning-user sign-in render the same panel but
// must not say the same thing: one confirms an address, the other finishes a
// sign-in.
const NAMESPACE_BY_VARIANT = {
  signIn: "onboardingV2.magicSignIn.checkEmail",
  verifyEmail: "onboardingV2.verifyEmail.checkEmail",
} as const;

interface MagicLinkSentPanelProps {
  email: string;
  onResend: () => Promise<void>;
  /** Omitted where there is no request form to return to, e.g. the confirm page. */
  onEdit?: () => void;
  variant?: keyof typeof NAMESPACE_BY_VARIANT;
}

/**
 * "Check your email" screen shown after a magic link is sent. Lives outside
 * the request page because the confirm page also renders it, after re-sending
 * a link from the expired-link state.
 */
export const MagicLinkSentPanel = ({
  email,
  onResend,
  onEdit,
  variant = "signIn",
}: MagicLinkSentPanelProps) => {
  const t = useTranslations(NAMESPACE_BY_VARIANT[variant]);
  const expiry = useCountdown(LINK_TTL_SECONDS);
  const cooldown = useCountdown(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | undefined>(undefined);
  // A state-only guard leaves a gap: `isResending` doesn't apply until the
  // next render, so a fast double-tap sends two emails and the second token
  // silently kills the first. Mirrors the confirm page's isResendingRef.
  const isResendingRef = useRef(false);

  // Gated separately from the in-flight state on purpose. Folding the two
  // together would fall through to the "try again in {seconds}" copy while a
  // resend is in flight, and the cooldown hasn't restarted yet at that point,
  // so it would read "try again in 0 seconds".
  const isCoolingDown = cooldown.secondsLeft > 0;
  // Amber while the link is still usable, error once it isn't - the chip is
  // the only signal that the emailed link is now dead.
  const isExpired = expiry.secondsLeft <= 0;

  const handleResend = async () => {
    if (isCoolingDown || isResendingRef.current) return;
    isResendingRef.current = true;
    setIsResending(true);
    setResendError(undefined);
    try {
      await onResend();
      expiry.restart();
      cooldown.restart();
    } catch (error) {
      console.error("Error resending magic link", error);
      setResendError(t("resendError"));
    } finally {
      setIsResending(false);
      isResendingRef.current = false;
    }
  };

  return (
    <OnboardingFormWrapper
      outerFooter={<AuthSupportFooter placement="outer" />}
      innerFooter={<AuthSupportFooter placement="inner" />}
    >
      <FormHeader title={t("title")} description={t("description")} />
      <Stack spacing={4}>
        <Stack spacing={3} sx={{ alignItems: "flex-start" }}>
          <Box
            sx={(theme) => ({
              width: "100%",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              gap: 2,
              paddingX: theme.spacing(4),
              paddingY: theme.spacing(3),
              borderRadius: theme.shape.borderRadius,
              backgroundColor: theme.vars!.palette.rmh.paper["4-faint"].rest,
            })}
          >
            <Typography variant="bodyMdRegular" sx={{ wordBreak: "break-all" }}>
              {email}
            </Typography>
            {onEdit && (
              <Link component="button" type="button" onClick={onEdit}>
                {t("edit")}
              </Link>
            )}
          </Box>
          <Chip
            label={
              isExpired
                ? t("linkExpired")
                : t("linkExpiresIn", {
                    countdown: formatCountdown(expiry.secondsLeft),
                  })
            }
            size="small"
            variant="outlined"
            sx={(theme) => {
              const tier = isExpired ? "9-errorFaint" : "13-warningFaint";
              return {
                backgroundColor: theme.vars!.palette.rmh.paper[tier].rest,
                borderColor: theme.vars!.palette.rmh.border[tier].rest,
                color: isExpired
                  ? theme.vars!.palette.rmh.text.error
                  : theme.vars!.palette.rmh.text.warning,
              };
            }}
          />
        </Stack>

        {resendError && <Alert severity="error">{resendError}</Alert>}

        <Stack spacing={1} sx={{ textAlign: "center" }}>
          <Typography variant="bodyMdRegular" color="text.secondary">
            {t("noLinkPrompt")}
          </Typography>
          <Typography variant="bodyMdRegular" color="text.secondary">
            {isCoolingDown
              ? t.rich("checkSpamOrWait", {
                  seconds: cooldown.secondsLeft,
                  highlight: (chunks) => (
                    <Typography
                      component="span"
                      variant="bodyMdMedium"
                      sx={(theme) => ({
                        color: theme.vars!.palette.rmh.text.interactiveLink,
                      })}
                    >
                      {chunks}
                    </Typography>
                  ),
                })
              : t.rich("checkSpamOrResend", {
                  // Link/component="button" rather than Button: this sits
                  // mid-sentence, and Button's padding and min-width open a
                  // visible gap around the phrase.
                  action: (chunks) => (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={handleResend}
                      disabled={isResending}
                      aria-busy={isResending}
                      sx={(theme) => ({
                        ...theme.typography.bodyMdMedium,
                        verticalAlign: "baseline",
                        // The in-flight window is the only time this renders
                        // disabled, so it doubles as the "sending" affordance.
                        "&:disabled": {
                          color: theme.vars!.palette.rmh.text.disabled,
                          textDecorationColor:
                            theme.vars!.palette.rmh.text.disabled,
                          cursor: "default",
                        },
                      })}
                    >
                      {chunks}
                    </Link>
                  ),
                })}
          </Typography>
          <Typography variant="bodySmRegular" color="text.secondary">
            {t("closeTabHint")}
          </Typography>
        </Stack>
      </Stack>
    </OnboardingFormWrapper>
  );
};
